import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CreateSchoolStepperProvider from 'providers/CreateSchoolWizard';
import { CREATE_SCHOOL_WIZARD } from 'enums';
import { CreateSchoolValues, StructureType } from 'types';
import CreateSchoolForm from './CreateSchool.form';
import { SchoolGroupStructureType, SchoolGroupType } from 'searchality-data';
import api from 'api';
import { useTranslation } from 'react-i18next';
import MainLayout from 'components/MainLayout';
import useAppDispatch from 'hooks/useAppDispatch';
import { loadCurrentUser } from 'store/slices/auth.slice';
import {
  popServerError,
  popSuccess,
} from 'store/slices/popNotifications.slice';
import { School } from 'models/School';

import './CreateSchool.styles.scss';
import utils from 'utils';

const CreateSchool: React.FC = () => {
  const navigate = useNavigate();

  const { pathname } = useLocation();

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const [initialStepIndex, setInitialStepIndex] = useState<number | null>(null);

  let [schoolType] = pathname.match(/school-type\/([a-z-]+)/i)?.slice(1) || [];
  schoolType = useParams().schoolType || schoolType;

  const [structureType] =
    pathname.match(/school-information\/([a-z-]+)/i)?.slice(1) || [];

  let [residentialType] =
    pathname.match(/residential-type\/([a-z-]+)/i)?.slice(1) || [];
  residentialType = useParams().residentialType || residentialType;

  const initData = useRef<CreateSchoolValues>({
    type: schoolType as SchoolGroupType,
    structureType: (structureType === 'district'
      ? 'group'
      : structureType) as StructureType,
    residentialType: utils.mapRouteToResidentialType(residentialType),
  }).current;

  const handleSubmit = useCallback(
    async (values: any) => {
      const {
        type: typeValue,
        structureType,
        schoolCount,
        schools,
        isPartOfAssociation,
        nationalAssociations,
        regionalAssociations,
        stateAssociations,
        associationContactDetails,
        ...restValues
      } = values;

      const type: string =
        typeValue === 'public-school' ? 'public school' : typeValue;

      const schoolsWithoutLogo = schools?.map((school: any) => {
        const { schoolLogo, ...rest } = school;

        return rest;
      });

      try {
        const { data } = await api.schools.createSchool({
          type,
          schoolCount: parseInt(schoolCount ?? schools.length),
          schools: schoolsWithoutLogo,
          structureType,
          ...restValues,
        });

        for (const school of data?.schools as School[]) {
          try {
            const schoolLogo = schools?.find(
              (el: any) => el.name === school.name,
            )?.schoolLogo;

            if (schoolLogo) {
              await api.user.uploadSchoolLogo(schoolLogo[0], school._id);
            }
          } catch (e) {
            dispatch(popServerError(e));
          }
        }

        dispatch(
          popSuccess(
            t('schoolCreateSuccess', {
              type:
                structureType === SchoolGroupStructureType.STANDALONE
                  ? t('account')
                  : t('group'),
            }),
          ),
        );
        setTimeout(() => {
          dispatch(loadCurrentUser());
          navigate('/get-started');
        }, 500);
      } catch (error) {
        dispatch(popServerError(error));
      }
    },
    [dispatch, navigate, t],
  );

  // since we are not patching data
  // we need to redirect form to the begining
  useEffect(() => {
    if (schoolType && pathname.includes(schoolType)) {
      setInitialStepIndex(CREATE_SCHOOL_WIZARD.SCHOOL_TYPE);
      return;
    }

    if (!pathname.includes('school-type'))
      navigate('school-type', { replace: true });
    // run this only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout className="create-school" headerShadow isGreen>
      <CreateSchoolStepperProvider initialStep={initialStepIndex}>
        <CreateSchoolForm onSubmit={handleSubmit} initialValues={initData} />
      </CreateSchoolStepperProvider>
    </MainLayout>
  );
};

export default CreateSchool;
